import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Search, Sparkles, ArrowRight, X } from "lucide-react";
import { useApp } from "../context/AppContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXAMPLES = [
  "Jouet pour enfant 4 ans qui ne parle pas",
  "Matériel pour dyslexie CE1",
  "Cartes pour la phonologie",
  "Ressource TDAH ado",
];

/**
 * Natural-language search bar powered by Claude.
 * Renders inline results (up to 8 products) directly under the input.
 */
export default function SemanticSearchBar() {
  const { fmt, lang } = useApp() || {};
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e?.preventDefault();
    if (!q.trim()) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/search/semantic`, { query: q.trim() });
      setResults(data);
    } catch {
      setResults({ items: [], filters: {}, count: 0, error: true });
    } finally {
      setLoading(false);
    }
  }

  function reset() { setQ(""); setResults(null); }

  return (
    <section className="max-w-3xl mx-auto px-6 py-8" data-testid="semantic-search">
      <div className="bg-white rounded-3xl border border-ink/10 shadow-sm p-5">
        <p className="text-xs uppercase tracking-[0.25em] text-ink/60 mb-3 inline-flex items-center gap-1.5">
          <Sparkles size={12} className="text-pedablue" /> Recherche IA · dis-nous ce que tu cherches
        </p>
        <form onSubmit={submit} className="flex items-center gap-2">
          <div className="flex-1 min-w-0 flex items-center bg-ivory-200 rounded-full px-4 py-2">
            <Search size={16} className="text-ink/50 flex-shrink-0" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Ex: jouet pour enfant 4 ans avec retard de langage"
              className="flex-1 min-w-0 bg-transparent outline-none px-3 py-1 text-sm"
              data-testid="semantic-input"
            />
            {q && <button type="button" onClick={reset} className="p-1 rounded-full hover:bg-white shrink-0" data-testid="semantic-clear"><X size={14} /></button>}
          </div>
          <button type="submit" disabled={!q.trim() || loading} className="px-4 py-2.5 rounded-full bg-ink text-white text-sm disabled:opacity-50 shrink-0 whitespace-nowrap" data-testid="semantic-submit">
            {loading ? "…" : "Chercher"}
          </button>
        </form>

        {!results && (
          <div className="mt-3 flex flex-wrap gap-2" data-testid="semantic-examples">
            {EXAMPLES.map((ex) => (
              <button key={ex} type="button" onClick={() => setQ(ex)}
                className="text-xs px-3 py-1.5 rounded-full bg-ivory-200 hover:bg-pedablue/10 text-ink-soft">
                {ex}
              </button>
            ))}
          </div>
        )}

        {results && (
          <div className="mt-5" data-testid="semantic-results">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-ink-soft">
                <strong className="text-ink">{results.count || 0}</strong> résultat{(results.count || 0) > 1 ? "s" : ""}
                {results.filters?.age && <> · âge <span className="text-pedablue">{results.filters.age}</span></>}
                {results.filters?.troubles?.length > 0 && <> · {results.filters.troubles.filter(t => t !== "null").join(", ")}</>}
              </p>
              <button type="button" onClick={reset} className="text-xs text-ink-soft hover:text-ink" data-testid="semantic-new-search">Nouvelle recherche</button>
            </div>
            {results.items?.length === 0 && (
              <p className="text-sm text-ink-soft italic text-center py-6" data-testid="semantic-empty">Aucun produit trouvé. Essayez une formulation différente ou explorez le catalogue.</p>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {(results.items || []).slice(0, 8).map((p) => {
                const name = (typeof p.name === "object" ? (p.name[lang] || p.name.fr) : p.name) || "";
                const img = (p.images && p.images[0]) || null;
                return (
                  <Link key={p.id} to={`/produit/${p.slug}`} className="group block bg-white border border-ink/10 rounded-2xl overflow-hidden hover:shadow-md transition" data-testid={`semantic-item-${p.slug}`}>
                    <div className="aspect-square bg-ivory-200">
                      {img && <img src={img} alt={name} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition" />}
                    </div>
                    <div className="p-2.5">
                      <p className="text-xs line-clamp-2 group-hover:text-coral">{name}</p>
                      <p className="text-xs font-medium mt-1">{fmt ? fmt(p.price) : `${p.price} €`}</p>
                    </div>
                  </Link>
                );
              })}
            </div>
            {results.items?.length > 0 && (
              <Link to="/catalogue" className="mt-4 inline-flex items-center gap-1 text-sm text-pedablue hover:underline" data-testid="semantic-see-all">
                Voir tout le catalogue <ArrowRight size={14} />
              </Link>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
